import type { Component } from "replo-runtime";
import type { SolidOrGradient } from "replo-runtime/shared/types";
import type { ContextRef, ProductRef } from "schemas/product";

import { DynamicDataTargetType } from "replo-runtime/shared/dynamicData";
import { getCurrentComponentContext } from "replo-runtime/shared/utils/context";
import {
  doesComponentHaveConnectedDynamicDataProps,
  doesComponentSupportDynamicDataProps,
  getPrimaryDynamicDataProp,
} from "replo-runtime/shared/utils/dynamic-data";
import { evaluateVariableAsString } from "replo-runtime/store/ReploVariable";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

import useApplyComponentAction from "./useApplyComponentAction";

const useDynamicDataQuickConnect = (component: Component | null) => {
  const applyComponentAction = useApplyComponentAction();
  if (!component) {
    return {
      supportsDynamicData: false,
      isDynamicDataConnected: false,
    };
  }
  const supportsDynamicData = doesComponentSupportDynamicDataProps(component);
  const isDynamicDataConnected =
    doesComponentHaveConnectedDynamicDataProps(component);
  const dynamicDataProp = getPrimaryDynamicDataProp(component);
  const handleChangePropValue = (
    newPropValue:
      | string
      | ContextRef
      | ProductRef
      | ProductRef[]
      | SolidOrGradient,
  ) => {
    if (!dynamicDataProp) {
      return null;
    }
    const { propKey, targetType } = dynamicDataProp;
    exhaustiveSwitch({ type: targetType })({
      [DynamicDataTargetType.TEXT]: () => {
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: newPropValue,
          },
        });
      },
      [DynamicDataTargetType.PRODUCT]: () => {
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: newPropValue,
          },
        });
      },
      [DynamicDataTargetType.PRODUCT_VARIANT]: () => {
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: newPropValue,
          },
        });
      },
      [DynamicDataTargetType.SELLING_PLAN]: () => {
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: newPropValue,
          },
        });
      },
      [DynamicDataTargetType.TEXT_COLOR]: () => {
        applyComponentAction({
          componentId: component.id,
          type: "setStyles",
          value: {
            color: "red",
          },
        });
      },
      [DynamicDataTargetType.URL]: () => {
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: newPropValue,
          },
        });
      },
      [DynamicDataTargetType.ANY_LIST]: () => {
        const transformedValue = {
          type: "inline",
          valueType: "dynamic",
          dynamicPath: newPropValue,
        };
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: transformedValue,
          },
        });
      },
      [DynamicDataTargetType.IMAGE_LIST]: () => {
        const transformedValue = {
          type: "inline",
          valueType: "dynamic",
          dynamicPath: newPropValue,
        };
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: transformedValue,
          },
        });
      },
      [DynamicDataTargetType.INTEGER]: () => {
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: newPropValue,
          },
        });
      },
      [DynamicDataTargetType.QUANTITY]: () => {
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: newPropValue,
          },
        });
      },
      [DynamicDataTargetType.IMAGE]: () => {
        applyComponentAction({
          componentId: component.id,
          type: "setStyles",
          value: {
            [propKey]: newPropValue,
          },
        });
      },
    });
  };
  const handleRemovePropValue = () => {
    if (!dynamicDataProp || !dynamicDataProp.propValue) {
      return;
    }
    const { propKey, targetType } = dynamicDataProp;

    exhaustiveSwitch({ type: targetType })({
      [DynamicDataTargetType.TEXT]: () => {
        const componentContext = getCurrentComponentContext(component.id);
        const evaluatedVariable =
          dynamicDataProp.propValue &&
          evaluateVariableAsString(
            dynamicDataProp.propValue as string,
            componentContext,
          );
        if (!evaluatedVariable) {
          applyComponentAction({
            componentId: component.id,
            type: "deleteProps",
            propName: propKey,
          });
        }
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: evaluatedVariable,
          },
        });
      },
      [DynamicDataTargetType.PRODUCT]: () => {
        applyComponentAction({
          componentId: component.id,
          type: "deleteProps",
          propName: propKey,
        });
      },
      [DynamicDataTargetType.PRODUCT_VARIANT]: () => {
        applyComponentAction({
          componentId: component.id,
          type: "deleteProps",
          propName: propKey,
        });
      },
      [DynamicDataTargetType.SELLING_PLAN]: () => {
        applyComponentAction({
          componentId: component.id,
          type: "deleteProps",
          propName: propKey,
        });
      },
      [DynamicDataTargetType.TEXT_COLOR]: () => {
        return null;
      },
      [DynamicDataTargetType.URL]: () => {
        const componentContext = getCurrentComponentContext(component.id);
        const evaluatedVariable =
          dynamicDataProp.propValue &&
          evaluateVariableAsString(
            dynamicDataProp.propValue as string,
            componentContext,
          );
        if (!evaluatedVariable) {
          return;
        }
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: evaluatedVariable,
          },
        });
      },
      [DynamicDataTargetType.ANY_LIST]: () => {
        applyComponentAction({
          componentId: component.id,
          type: "deleteProps",
          propName: propKey,
        });
      },
      [DynamicDataTargetType.IMAGE_LIST]: () => {
        applyComponentAction({
          componentId: component.id,
          type: "deleteProps",
          propName: propKey,
        });
      },
      [DynamicDataTargetType.INTEGER]: () => {
        const componentContext = getCurrentComponentContext(component.id);
        const evaluatedVariable =
          dynamicDataProp.propValue &&
          evaluateVariableAsString(
            dynamicDataProp.propValue as string,
            componentContext,
          );
        if (!evaluatedVariable) {
          return;
        }
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: evaluatedVariable,
          },
        });
      },
      [DynamicDataTargetType.QUANTITY]: () => {
        const componentContext = getCurrentComponentContext(component.id);
        const evaluatedVariable =
          dynamicDataProp.propValue &&
          evaluateVariableAsString(
            dynamicDataProp.propValue as string,
            componentContext,
          );
        if (!evaluatedVariable) {
          applyComponentAction({
            componentId: component.id,
            type: "deleteProps",
            propName: propKey,
          });
        }
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: evaluatedVariable,
          },
        });
      },
      [DynamicDataTargetType.IMAGE]: () => {
        const componentContext = getCurrentComponentContext(component.id);
        const evaluatedVariable =
          dynamicDataProp.propValue &&
          evaluateVariableAsString(
            dynamicDataProp.propValue as string,
            componentContext,
          );
        if (!evaluatedVariable) {
          applyComponentAction({
            type: "applyCompositeAction",
            value: [
              {
                type: "setStyles",
                value: { __imageSource: null },
              },
              {
                type: "deleteProps",
                propName: "src",
                analyticsExtras: { actionType: "edit", createdBy: "replo" },
              },
            ],
          });
        }
        applyComponentAction({
          componentId: component.id,
          type: "setStyles",
          value: {
            [propKey]: evaluatedVariable,
          },
        });
      },
    });
  };
  return {
    supportsDynamicData,
    isDynamicDataConnected,
    dynamicDataProp,
    handleChangePropValue,
    handleRemovePropValue,
  };
};

export default useDynamicDataQuickConnect;
